import React, { useState } from "react";
import {
    CategorizedCheckboxDropdown,
    ClearableCheckboxDropdown,
    Icon,
    Table,
    TimestampFilters,
} from "dyl-components";
import { Table as SemanticTable } from "semantic-ui-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    KEY_CONTACT_ACCOUNT,
    KEY_DATE,
    KEY_PHONE_NUMBER,
    KEY_USER,
    KEY_DURATION,
    KEY_ACTIONS,
    KEY_DIRECTION,
    KEY_SYSTEM_RESULT,
    KEY_NOTE,
    KEY_CALL_TAGS,
    KEY_DESTINATION,
    KEY_USER_RESULT,
    KEY_CALL_TYPE,
} from "shared/constants/CALL_HISTORY_LOGS_COLUMNS";
import PropTypes from "prop-types";
import { PhoneUtil } from "utils";
import {
    getData,
    onToggleTimeUnit,
    useRangeCalendar,
} from "shared/CustomDateRange/customDateRange";

const SortActions = ({ direction = false, onToggleSort = () => {} }) => {
    return (
        <div
            onClick={onToggleSort}
            style={{ display: "inline-flex", cursor: "pointer" }}
        >
            <Icon
                name="fa-solid fa-arrow-down-long"
                style={{
                    marginRight: -7,
                    opacity: !direction ? "100%" : "50%",
                }}
            />
            <Icon
                name="fa-solid fa-arrow-up-long"
                style={{ opacity: direction ? "100%" : "50%" }}
            />
        </div>
    );
};

const CallHistoryLogHeader = ({
    isColumnHidden,
    root,
    onFilter,
    contactAccountOptions,
    isReadingContacts,
    isTimeline,
}) => {
    const isTimelineTab = root === "contact" || root === "account";
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dateOrder = Boolean(params.get("order") === "asc");
    const {
        dateRange,
        setDateRange,
        isCustomDateRangeOpen,
        setCustomDateRangeOpen,
        userAuthTimezone,
        setTimestamp,
        timestamp,
    } = useRangeCalendar(params, false);

    const onDateFilterChange = () => {
        const query = new URLSearchParams(params);
        const order = !dateOrder ? "asc" : "desc";
        query.set("order", order);
        const query_string = query.toString();
        navigate(
            `${location.pathname}${query_string ? `?${query_string}` : ""}`
        );
    };

    const onDateRangeChange = (_, { value }) => {
        setDateRange(value);
    };

    const [isTimeRangeOpen, setTimeRangeOpen] = useState(false);

    return (
        <Table.Row>
            <SemanticTable.HeaderCell style={{ paddingLeft: 15 }}/>
            {!isColumnHidden(KEY_DATE) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__SmallFixedWidth">
                    Date{" "}
                    {isTimelineTab && (
                        <TimestampFilters
                            timestamp={timestamp}
                            areTimeUnitOptionsOpen={isTimeRangeOpen}
                            onOpenTimeUnitOptions={() => setTimeRangeOpen(true)}
                            onCloseTimeUnitOptions={() => setTimeRangeOpen(false)}
                            isCustomDateRangeOpen={isCustomDateRangeOpen}
                            onOpenCustomDateRange={() => {
                                setCustomDateRangeOpen(true);
                                setDateRange('');
                            }}
                            onCloseCustomDateRange={() =>
                                setCustomDateRangeOpen(false)
                            }
                            onToggleTimeUnit={(timestamp) =>
                                onToggleTimeUnit(
                                    timestamp,
                                    setTimestamp,
                                    params,
                                    navigate,
                                    location.pathname
                                )
                            }
                            dateRange={dateRange}
                            onDateRangeChange={onDateRangeChange}
                            getData={() =>
                                getData({
                                    setCustomDateRangeOpen,
                                    dateRange,
                                    userAuthTimezone,
                                    params,
                                    navigate,
                                    url: location.pathname,
                                })
                            }
                        />
                    )}
                    <SortActions
                        direction={dateOrder}
                        onToggleSort={onDateFilterChange}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CONTACT_ACCOUNT) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__MediumLargeFixedWidth">
                    {root === "account" ? (
                        <CategorizedCheckboxDropdown
                            text="Contact/Account"
                            name="contact_id"
                            onChange={(_, { value }) => {
                                onFilter({ name: "contact_id", value });
                            }}
                            value={[
                                ...(params?.get?.("contact_id")?.split?.(",") ||
                                    []),
                                ...(params?.get?.("account_id")?.split?.(",") ||
                                    []),
                            ]
                                .map((id) => Number(id))
                                .filter((id) => !isNaN(id))}
                            categories={contactAccountOptions}
                            closeOnLoad
                            disabled={isReadingContacts}
                            loading={isReadingContacts}
                        />
                    ) : (
                        "Contact/Account"
                    )}
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_PHONE_NUMBER) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__SmallFixedWidth">
                    Phone Number
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_USER) && (
                <SemanticTable.HeaderCell>User</SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_DIRECTION) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__SmallFixedWidth">
                    {isTimelineTab ? (
                        <ClearableCheckboxDropdown
                            text="Direction"
                            name="direction"
                            options={PhoneUtil.DIRECTION_OPTIONS.filter(
                                (option) => option.value !== "internal"
                            )}
                            onChange={(_, { value }) =>
                                onFilter({ name: "direction", value })
                            }
                            value={
                                params?.get?.("direction")?.split?.(",") || []
                            }
                            closeOnLoad
                        />
                    ) : (
                        "Direction"
                    )}
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CALL_TYPE) && (
                <SemanticTable.HeaderCell>Call Type</SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_SYSTEM_RESULT) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__MediumFixedWidth">
                    {isTimelineTab ? (
                        <ClearableCheckboxDropdown
                            text="System Result"
                            name="system_result"
                            options={PhoneUtil.SYSTEM_RESULT_OPTIONS}
                            onChange={(_, { value }) => {
                                onFilter({ name: "system_result", value });
                            }}
                            value={
                                params?.get?.("system_result")?.split(",") || []
                            }
                            closeOnLoad
                        />
                    ) : (
                        "System Result"
                    )}
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_USER_RESULT) && !isTimeline &&(
                <SemanticTable.HeaderCell className="CallHistoryTable__SmallFixedWidth">
                    {isTimelineTab ? (
                        <ClearableCheckboxDropdown
                            text="User Result"
                            name="user_result"
                            options={PhoneUtil.CALL_RESULT_OPTIONS}
                            onChange={(_, { value }) =>
                                onFilter({ name: "user_result", value })
                            }
                            value={
                                params?.get?.("user_result")?.split(",") || []
                            }
                            closeOnLoad
                        />
                    ) : (
                        "User Result"
                    )}
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_DURATION) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__SmallFixedWidth">
                    Duration
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_DESTINATION) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__MediumFixedWidth">
                    Destination
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CALL_TAGS) && (
                <SemanticTable.HeaderCell>Call Tags</SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_NOTE) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__MediumFixedWidth">
                    Note
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_ACTIONS) && (
                <SemanticTable.HeaderCell className="CallHistoryTable__SmallFixedWidth">
                    Actions
                </SemanticTable.HeaderCell>
            )}
        </Table.Row>
    );
};

CallHistoryLogHeader.propTypes = {
    isColumnHidden: PropTypes.func.isRequired,
    root: PropTypes.string.isRequired,
    onFilter: PropTypes.func.isRequired,
    contactAccountOptions: PropTypes.array,
    isReadingContacts: PropTypes.bool,
};

export default CallHistoryLogHeader;
